import type { MouseEvent as ReactMouseEvent } from 'react';

type ProjectRef = string | { slug?: string; _id?: string } | null | undefined;

type ChangeLike = {
    _id?: string;
    project?: ProjectRef;
} | null | undefined;

function projectNavKey(project?: ProjectRef): string {
    if (!project)
        return '';
    if (typeof project === 'string')
        return project;
    return project.slug || project._id || '';
}

/** Standalone change URL (same shape as `Project.path(..., 'change', id)`). */
export function changeStandalonePath(change?: ChangeLike, projectFallback?: ProjectRef): string | null {
    if (!change?._id)
        return null;
    const key = projectNavKey(change.project || projectFallback);
    if (!key)
        return null;
    return `/projects/${key}/change/${change._id}`;
}

/** Open the standalone change page in a new browser tab. */
export function openChangeInNewBrowserTab(change?: ChangeLike, projectFallback?: ProjectRef) {
    const path = changeStandalonePath(change, projectFallback);
    if (!path)
        return;
    window.open(path, '_blank', 'noopener,noreferrer');
}

const NON_TEXT_INPUT_TYPES = new Set([
    'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

function focusedTextField(): HTMLElement | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement))
        return null;
    if (active.isContentEditable)
        return active;
    if (active instanceof HTMLTextAreaElement)
        return active;
    if (active instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.has(active.type))
        return active;
    return null;
}

/**
 * Linux middle-click pastes the primary selection into the focused field
 * (the search box, while a result is opened in a new tab). Swallow that paste.
 */
function suppressMiddleClickPaste(field: HTMLElement) {
    const blockPaste = (event: Event) => {
        if (event.type === 'beforeinput' && (event as InputEvent).inputType !== 'insertFromPaste')
            return;
        event.preventDefault();
        event.stopImmediatePropagation();
    };
    field.addEventListener('paste', blockPaste, true);
    field.addEventListener('beforeinput', blockPaste, true);
    const cleanup = () => {
        field.removeEventListener('paste', blockPaste, true);
        field.removeEventListener('beforeinput', blockPaste, true);
    };
    window.addEventListener('mouseup', () => {
        window.setTimeout(cleanup, 0);
    }, { capture: true, once: true });
}

/** Prevent the browser autoscroll gesture on middle-click. */
export function preventMiddleClickAutoscroll(event: ReactMouseEvent) {
    if (event.button !== 1)
        return;
    event.preventDefault();
    const field = focusedTextField();
    if (!field)
        return;
    suppressMiddleClickPaste(field);
    field.blur();
}

/** Middle-click handler: open the change in a new tab. */
export function handleChangeMiddleClick(
    event: ReactMouseEvent,
    change?: ChangeLike,
    projectFallback?: ProjectRef,
) {
    if (event.button !== 1 || !change?._id)
        return;
    event.preventDefault();
    event.stopPropagation();
    openChangeInNewBrowserTab(change, projectFallback);
}
